import React, { useEffect, useState } from 'react';
import { listApprovedReviews, submitReview } from '../services/reviews';

const ProductDetailsModal = ({ product, onClose, onAddToCart, onOrderNow, onNotifyCenter }) => {
  const price = product?.offer_price || product?.price || 0;
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ name: '', rating: 5, comment: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await listApprovedReviews(product.id);
        setReviews(data);
      } catch (_e) {}
    })();
  }, [product?.id]);

  if (!product) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl sm:text-2xl font-bold bengali-text text-slate-900">{product.name_bn}</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 p-2 rounded-lg hover:bg-slate-100">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 space-y-4">
          <div className="w-full h-56 sm:h-72 md:h-80 rounded-xl overflow-hidden border">
            <img src={product.image} alt={product.name_bn} className="w-full h-full object-cover" />
          </div>

          <div className="flex items-center justify-between">
            <div className="bengali-text">
              {product.offer_price ? (
                <>
                  <div className="text-2xl sm:text-3xl font-bold text-orange-600">৳{product.offer_price}</div>
                  <div className="text-sm sm:text-base text-gray-400 line-through">৳{product.price}</div>
                </>
              ) : (
                <div className="text-2xl sm:text-3xl font-bold text-orange-600">৳{product.price}</div>
              )}
            </div>
            {product.category && (
              <span className="px-3 py-1 rounded-full text-xs sm:text-sm bg-orange-100 text-orange-700 font-semibold bengali-text border">{product.category}</span>
            )}
          </div>

          {product.description_bn && (
            <div className="text-slate-700 leading-relaxed bengali-text whitespace-pre-line">
              {product.description_bn}
            </div>
          )}

          {/* Reviews */}
          <div className="mt-4">
            <h3 className="text-lg font-semibold text-slate-900 mb-2 bengali-text">ক্রেতার মতামত</h3>
            {reviews.length === 0 ? (
              <p className="text-sm text-slate-500">এখনও কোন রিভিউ নেই। প্রথম রিভিউ দিন!</p>
            ) : (
              <div className="space-y-3">
                {reviews.map((r) => (
                  <div key={r.id} className="border rounded-xl p-3 bg-slate-50">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-800">{r.customer_name}</span>
                      <span className="text-orange-600 font-semibold">★ {r.rating}/5</span>
                    </div>
                    <p className="text-slate-700 mt-1 whitespace-pre-line">{r.comment}</p>
                    {r.admin_reply && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                        <div className="text-xs text-green-700 font-semibold">Admin Reply</div>
                        <div className="text-sm text-green-800 whitespace-pre-line">{r.admin_reply}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Review */}
          <div className="mt-4 border-t pt-4">
            <h4 className="text-base font-semibold text-slate-900 mb-2 bengali-text">রিভিউ দিন</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="আপনার নাম"
                className="px-4 py-3 border rounded-lg"
                value={reviewForm.name}
                onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
              />
              <select
                className="px-4 py-3 border rounded-lg"
                value={reviewForm.rating}
                onChange={(e) => setReviewForm({ ...reviewForm, rating: parseInt(e.target.value, 10) })}
              >
                {[5,4,3,2,1].map((n) => (
                  <option key={n} value={n}>রেটিং: {n}</option>
                ))}
              </select>
              <div className="sm:col-span-2">
                <textarea
                  rows={3}
                  placeholder="আপনার মতামত লিখুন"
                  className="w-full px-4 py-3 border rounded-lg"
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <button
                  disabled={submitting || !reviewForm.name || !reviewForm.comment}
                  onClick={async () => {
                    try {
                      setSubmitting(true);
                      await submitReview(product.id, reviewForm.name.trim(), reviewForm.rating, reviewForm.comment.trim());
                      setReviewForm({ name: '', rating: 5, comment: '' });
                      if (onNotifyCenter) onNotifyCenter('রিভিউ জমা হয়েছে। অনুমোদনের জন্য অপেক্ষা করুন।', 4000);
                    } catch (e) {
                      if (onNotifyCenter) onNotifyCenter(e.message || 'রিভিউ জমা দিতে ব্যর্থ', 4000);
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                  className={`w-full px-6 py-3 rounded-lg text-white font-semibold ${submitting ? 'bg-slate-400' : 'bg-slate-800 hover:bg-slate-900'}`}
                >
                  {submitting ? 'জমা হচ্ছে...' : 'রিভিউ সাবমিট করুন'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t bg-white/70 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => onAddToCart && onAddToCart(product)}
            className="flex-1 px-6 py-3 rounded-lg border-2 border-orange-500 text-orange-600 hover:bg-orange-50 font-semibold bengali-text"
          >
            ঝুড়িতে নিন
          </button>
          <button
            onClick={() => onOrderNow && onOrderNow(product)}
            className="flex-1 px-6 py-3 rounded-lg bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white font-semibold bengali-text"
            style={{ boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)' }}
          >
            অর্ডার করুন
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsModal;


